import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Minus, Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../utils/currency';

const CartSidebar = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { cart, loading, updateCartItem, removeFromCart } = useCart();

  // Per-item action lock. Keys are `${productId}::${variantId||''}::${action}`
  // so quantity +/− and remove can each spin independently while the network
  // request is in-flight. Prevents duplicate submissions on double-clicks.
  const [busyKeys, setBusyKeys] = useState(new Set());
  const keyFor = (productId, variantId, action) =>
    `${productId}::${variantId || ''}::${action}`;
  const isBusy = (productId, variantId, action) =>
    busyKeys.has(keyFor(productId, variantId, action));
  const itemHasAnyBusy = (productId, variantId) =>
    busyKeys.has(keyFor(productId, variantId, 'inc')) ||
    busyKeys.has(keyFor(productId, variantId, 'dec')) ||
    busyKeys.has(keyFor(productId, variantId, 'remove'));

  const runWithLock = async (productId, variantId, action, fn) => {
    const key = keyFor(productId, variantId, action);
    setBusyKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    try {
      await fn();
    } finally {
      setBusyKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const currentLang = i18n.language || 'en';
  const cartItems = cart?.items || [];
  const subtotal = cart?.subtotal || 0;

  const handleQuantityChange = async (productId, newQuantity, variantId = null, direction = 'inc') => {
    if (newQuantity < 1) return;
    await runWithLock(productId, variantId, direction, () =>
      updateCartItem(productId, newQuantity, variantId)
    );
  };

  const handleRemoveItem = async (productId, variantId = null) => {
    await runWithLock(productId, variantId, 'remove', () =>
      removeFromCart(productId, variantId)
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:w-[400px] flex flex-col p-0">
        <SheetHeader className="border-b p-6">
          <SheetTitle className="text-xl font-semibold">
            {t('cart.myCart')} ({cartItems.length})
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
          </div>
        ) : cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg
                className="w-16 h-16 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-900 mb-2">
              {t('cart.emptyCart')}
            </p>
            <Link to="/shop" onClick={onClose}>
              <Button className="mt-4 bg-[#be1f1a] hover:bg-[#a71b17] text-white">
                {t('cart.continueShopping')}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 p-6">
              <div className="space-y-4">
                {cartItems.map((item, index) => (
                  <div
                    key={`${item.productId}-${item.variantId || ''}-${index}`}
                    className="flex gap-4 pb-4 border-b border-gray-200"
                  >
                    <img
                      src={item.image?.thumbnail || item.image?.medium || 'https://placehold.co/80x100'}
                      alt={item.name?.[currentLang] || item.name?.en || t('cartSidebarMore.product')}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-sm mb-1">
                        {item.name?.[currentLang] || item.name?.en || t('cartSidebarMore.product')}
                      </h4>
                      {item.size && item.size !== 'Única' && (
                        <p className="text-xs text-gray-500 mb-2">
                          {t('checkout.size')}: {item.size}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center border rounded">
                          <button
                            onClick={() =>
                              handleQuantityChange(item.productId, item.quantity - 1, item.variantId, 'dec')
                            }
                            className="px-2 py-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={item.quantity <= 1 || itemHasAnyBusy(item.productId, item.variantId)}
                            data-testid={`cart-item-decrease-${item.productId}`}
                          >
                            {isBusy(item.productId, item.variantId, 'dec') ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Minus className="h-3 w-3" />
                            )}
                          </button>
                          <span className="px-3 py-1 border-x text-sm">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              handleQuantityChange(item.productId, item.quantity + 1, item.variantId, 'inc')
                            }
                            className="px-2 py-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={itemHasAnyBusy(item.productId, item.variantId)}
                            data-testid={`cart-item-increase-${item.productId}`}
                          >
                            {isBusy(item.productId, item.variantId, 'inc') ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                        <p className="font-semibold text-[#be1f1a]">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item.productId, item.variantId)}
                      className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={t('cart.remove')}
                      disabled={itemHasAnyBusy(item.productId, item.variantId)}
                      data-testid={`cart-item-remove-${item.productId}`}
                    >
                      {isBusy(item.productId, item.variantId, 'remove') ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Trash2 className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t p-6 space-y-4">
              <div className="flex justify-between items-center text-lg font-semibold">
                <span>{t('cart.subtotal')}:</span>
                <span className="text-[#be1f1a]">{formatPrice(subtotal)}</span>
              </div>
              <Link to="/checkout" onClick={onClose}>
                <Button className="w-full bg-[#be1f1a] hover:bg-[#a71b17] text-white h-12">
                  {t('cart.checkout')}
                </Button>
              </Link>
              <Button
                variant="outline"
                className="w-full"
                onClick={onClose}
              >
                {t('cart.continueShopping')}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSidebar;
